import { useState } from 'react';
import { Alert, Button, Form, Input, Modal, Space } from 'antd';
import {
  EmailAuthProvider,
  getAuth,
  GithubAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  SAMLAuthProvider,
  TwitterAuthProvider,
  User,
} from 'firebase/auth';
import { getSsoProviderKind } from '@todesktop/shared';

const providers = [
  { id: 'google.com', name: 'Google', create: () => new GoogleAuthProvider() },
  { id: 'github.com', name: 'GitHub', create: () => new GithubAuthProvider() },
  {
    id: 'twitter.com',
    name: 'Twitter',
    create: () => new TwitterAuthProvider(),
  },
];

export function reauthenticateForDeletion(user: User): Promise<void> {
  const { uid, email } = user;
  const assertSameUser = () => {
    const current = getAuth().currentUser;
    if (current?.uid !== uid || current?.email !== email) {
      throw new Error('Your account changed. Refresh the page and try again.');
    }
  };
  const linked = new Set(user.providerData.map(({ providerId }) => providerId));
  // A team's single sign-on (Okta and other SAML or OIDC providers).
  const ssoProviders = [...linked].flatMap((id) => {
    const kind = getSsoProviderKind(id);
    if (!kind) return [];
    return [
      {
        id,
        name: 'single sign-on',
        create: () =>
          kind === 'saml' ? new SAMLAuthProvider(id) : new OAuthProvider(id),
      },
    ];
  });
  const oauthProviders = [
    ...ssoProviders,
    ...providers.filter(({ id }) => linked.has(id)),
  ];
  const hasPassword = linked.has('password') && Boolean(email);
  if (!hasPassword && oauthProviders.length === 0) {
    return Promise.reject(
      new Error(
        'Unable to confirm your identity with this sign-in method. Please contact support to delete your account.',
      ),
    );
  }

  return new Promise((resolve, reject) => {
    let cancelled = false;
    const cancel = () => {
      cancelled = true;
      reject(new Error('Account deletion cancelled.'));
    };
    function Confirmation() {
      const [busy, setBusy] = useState(false);
      const [error, setError] = useState('');
      const confirm = async (reauthenticate: () => Promise<unknown>) => {
        setBusy(true);
        setError('');
        try {
          assertSameUser();
          await reauthenticate();
          if (cancelled) return;
          assertSameUser();
          await user.getIdToken(true);
          assertSameUser();
          if (cancelled) return;
          modal.destroy();
          resolve();
        } catch (cause) {
          if (!cancelled) {
            setError(
              cause instanceof Error
                ? cause.message
                : 'Unable to confirm your identity. Please try again.',
            );
          }
        } finally {
          if (!cancelled) setBusy(false);
        }
      };
      return (
        <Space orientation="vertical" style={{ width: '100%' }}>
          <p>Sign in again to confirm it’s you before deleting your account.</p>
          {error && <Alert type="error" title={error} />}
          {hasPassword && (
            <Form
              onFinish={({ password }: { password: string }) =>
                confirm(() =>
                  reauthenticateWithCredential(
                    user,
                    EmailAuthProvider.credential(email!, password),
                  ),
                )
              }
            >
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: 'Enter your password.' }]}
              >
                <Input.Password
                  autoComplete="current-password"
                  disabled={busy}
                />
              </Form.Item>
              <Button htmlType="submit" danger loading={busy}>
                Confirm deletion
              </Button>
            </Form>
          )}
          {oauthProviders.map(({ id, name, create }) => (
            <Button
              key={id}
              disabled={busy}
              onClick={() =>
                confirm(() => reauthenticateWithPopup(user, create()))
              }
            >
              Continue with {name}
            </Button>
          ))}
          <Button
            onClick={() => {
              cancel();
              modal.destroy();
            }}
          >
            Cancel
          </Button>
        </Space>
      );
    }
    const modal = Modal.confirm({
      title: 'Confirm account deletion',
      icon: null,
      content: <Confirmation />,
      footer: null,
      onCancel: cancel,
    });
  });
}
